import React from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { PageHeader, TabList, Toolbar, ResponsiveTable, TableSkeleton, EmptyState, Kpi, SectionCard, ReasonModal, Drawer, AutoBadge, StatusBadge, DateCell, Pagination, CardSkeleton } from "@/admin/ui";
import { RefreshCw, PlayCircle, ShieldAlert, Search, ClipboardCheck, PlayIcon } from "lucide-react";

const fmtNum = (n) => Number(n || 0).toLocaleString();

function OverviewTab() {
  const { t } = useLang();
  const [stats, setStats] = React.useState(null);
  const [legacy, setLegacy] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const load = React.useCallback(async () => {
    const [s, l] = await Promise.all([
      api.get("/api/admin/reconciliation/stats").then((r) => r.data),
      api.get("/api/admin/reconciliation").then((r) => r.data),
    ]);
    setStats(s); setLegacy(l);
  }, []);
  React.useEffect(() => { load(); }, [load]);
  const runNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/api/admin/reconciliation/runs");
      toast.success(`Scanned ${r.data.total_users_scanned} users, ${r.data.total_users_with_drift} with drift`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setBusy(false); }
  };
  if (!stats || !legacy) return <CardSkeleton h="h-40" />;
  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <button className="lp-btn-ghost" onClick={load} data-testid="recon-refresh" aria-label="Refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
        <button className="lp-btn-primary text-xs py-1.5 px-3" onClick={runNow} disabled={busy} data-testid="recon-run-now">
          <PlayCircle className="w-3.5 h-3.5" /> {t("admin.recon.run_now")}
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label={t("admin.recon.findings_open")} value={fmtNum(stats.findings_open)} tone={stats.findings_open ? "warning" : "success"} testid="kpi-open" />
        <Kpi label={t("admin.recon.findings_critical")} value={fmtNum(stats.findings_critical_open)} tone={stats.findings_critical_open ? "danger" : "success"} testid="kpi-critical" />
        <Kpi label={t("admin.recon.requests_pending")} value={fmtNum(stats.requests_pending_first + stats.requests_pending_second)} tone={(stats.requests_pending_first + stats.requests_pending_second) ? "warning" : "success"} testid="kpi-pending" />
        <Kpi label={t("admin.recon.requests_executed")} value={fmtNum(stats.requests_executed)} tone="success" testid="kpi-executed" />
        <Kpi label={t("admin.recon.orphan")} value={fmtNum(legacy.orphan_invoices.count)} tone={legacy.orphan_invoices.count ? "danger" : "success"} testid="kpi-orphan" />
        <Kpi label={t("admin.recon.violations")} value={fmtNum(legacy.refund_cap_violations.count)} tone={legacy.refund_cap_violations.count ? "danger" : "success"} testid="kpi-violations" />
        <Kpi label={t("admin.recon.stale_freezes")} value={fmtNum(legacy.stale_freezes.count)} tone={legacy.stale_freezes.count ? "warning" : "success"} testid="kpi-stale" />
        <Kpi label={t("admin.recon.failed_hooks")} value={fmtNum(legacy.failed_webhooks)} tone={legacy.failed_webhooks ? "danger" : "success"} testid="kpi-hooks" />
      </div>
      {stats.last_run && (
        <SectionCard title={t("admin.recon.last_run")}>
          <div className="text-white/85 text-sm"><DateCell value={stats.last_run.started_at} /> → <DateCell value={stats.last_run.completed_at} /></div>
          <div className="text-white/60 text-xs mt-1">scanned {stats.last_run.total_users_scanned} · drift users {stats.last_run.total_users_with_drift} · total abs {fmtNum(stats.last_run.total_absolute_drift)} · status {stats.last_run.status}</div>
        </SectionCard>
      )}
    </div>
  );
}

function FindingsTab({ onOpenFinding }) {
  const { t } = useLang();
  const [items, setItems] = React.useState(null);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [severity, setSeverity] = React.useState("");
  const [resolved, setResolved] = React.useState("false");
  const [q, setQ] = React.useState("");
  const perPage = 25;
  const load = React.useCallback(async () => {
    const params = { page, per_page: perPage };
    if (severity) params.severity = severity;
    if (resolved === "true") params.resolved = true;
    if (resolved === "false") params.resolved = false;
    if (q) params.user_email = q;
    const r = await api.get("/api/admin/reconciliation/findings", { params });
    setItems(r.data.items); setTotal(r.data.total);
  }, [page, severity, resolved, q]);
  React.useEffect(() => { load(); }, [load]);
  const pages = Math.max(1, Math.ceil(total / perPage));
  return (
    <div className="space-y-4">
      <Toolbar right={<div className="text-xs text-white/40">{total} {t("admin.recon.findings_total")}</div>}>
        <div className="flex-1 relative min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input className="lp-input w-full pl-9 py-2 text-sm" placeholder={t("admin.recon.search_ph")}
            value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="findings-search" />
        </div>
        <select className="lp-input text-xs py-1.5" value={severity} onChange={(e) => { setSeverity(e.target.value); setPage(1); }} data-testid="findings-severity-filter">
          <option value="">{t("admin.recon.filter.all_severity")}</option>
          <option value="critical">critical</option><option value="high">high</option>
          <option value="medium">medium</option><option value="low">low</option>
        </select>
        <select className="lp-input text-xs py-1.5" value={resolved} onChange={(e) => { setResolved(e.target.value); setPage(1); }} data-testid="findings-resolved-filter">
          <option value="false">{t("admin.recon.filter.open")}</option>
          <option value="true">{t("admin.recon.filter.resolved")}</option>
          <option value="">{t("admin.recon.filter.all")}</option>
        </select>
      </Toolbar>
      {!items ? <TableSkeleton rows={8} cols={8} /> : items.length === 0 ? (
        <EmptyState title={t("admin.recon.empty_findings")} />
      ) : (
        <ResponsiveTable minWidth={1000}>
          <thead className="lp-table-sticky"><tr>
            <th>{t("admin.recon.col.user")}</th>
            <th className="text-right">{t("admin.recon.col.stored")}</th>
            <th className="text-right">{t("admin.recon.col.ledger")}</th>
            <th className="text-right">{t("admin.recon.col.drift")}</th>
            <th>{t("admin.recon.col.severity")}</th>
            <th>{t("admin.recon.col.source")}</th>
            <th>{t("admin.recon.col.status")}</th>
            <th>{t("admin.recon.col.detected")}</th>
          </tr></thead>
          <tbody>
            {items.map((f) => (
              <tr key={f.id} className="cursor-pointer hover:bg-white/5" onClick={() => onOpenFinding(f)} data-testid={`finding-row-${f.id.slice(0, 6)}`}>
                <td className="font-mono text-white/85 text-xs truncate max-w-[220px]" title={f.user_email}>{f.user_email}</td>
                <td className="text-right text-white/80 tabular-nums">{fmtNum(f.stored_balance)}</td>
                <td className="text-right text-white/60 tabular-nums">{fmtNum(f.ledger_balance)}</td>
                <td className={`text-right font-bold tabular-nums ${f.drift_amount > 0 ? "text-red-300" : f.drift_amount < 0 ? "text-amber-300" : "text-white/50"}`}>
                  {f.drift_amount > 0 ? "+" : ""}{fmtNum(f.drift_amount)}
                </td>
                <td><AutoBadge value={f.severity} /></td>
                <td className="text-white/60 text-xs">{f.suspected_source || "—"}</td>
                <td>{f.resolved ? <StatusBadge tone="success">resolved</StatusBadge> : <StatusBadge tone="warning">{f.investigation_status || "unstarted"}</StatusBadge>}</td>
                <td><DateCell value={f.first_detected_at} /></td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      )}
      <Pagination page={page} totalPages={pages} onChange={setPage} total={total} unitLabel={t("admin.recon.findings_total")} />
    </div>
  );
}

function FindingDrawer({ finding, onClose, refresh }) {
  const { t } = useLang();
  const [detail, setDetail] = React.useState(null);
  const [dry, setDry] = React.useState(null);
  const [modal, setModal] = React.useState(null);
  React.useEffect(() => {
    if (!finding) return;
    setDetail(null); setDry(null);
    api.get(`/api/admin/reconciliation/findings/${finding.id}`).then((r) => setDetail(r.data));
  }, [finding]);
  if (!finding) return null;
  const runDryRun = async () => {
    try { const r = await api.post(`/api/admin/reconciliation/findings/${finding.id}/dry-run`); setDry(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };
  const requestResolution = () => {
    const proposed = dry?.proposed_adjustment ?? finding.drift_amount;
    const isMega = Math.abs(proposed) >= 1_000_000;
    setModal({
      title: t("admin.recon.resolution_request_title"),
      description: `${finding.user_email} · ${t("admin.recon.proposed")} ${proposed > 0 ? "+" : ""}${fmtNum(proposed)}`,
      actionLabel: t("admin.recon.request_action"), minLen: 10,
      requireTyped: isMega, typedMatch: `${finding.user_email} · ${proposed}`,
      extras: <>
        <div className="rounded-lg bg-white/5 border border-white/10 p-3 text-xs space-y-1">
          <div className="text-white/50">Adjustment is a compensating immutable ledger entry, not a re-issuance of {fmtNum(proposed)} credits.</div>
          {dry && (<>
            <div>Stored: <span className="text-white/90 font-mono">{fmtNum(dry.current_stored_balance)}</span></div>
            <div>Ledger: <span className="text-white/90 font-mono">{fmtNum(dry.current_ledger_balance)}</span></div>
            <div>Drift: <span className="font-mono text-amber-200">{dry.drift_amount > 0 ? "+" : ""}{fmtNum(dry.drift_amount)}</span></div>
            <div>Projected ledger after: <span className="font-mono text-emerald-200">{fmtNum(dry.projected_ledger_balance_after)}</span></div>
          </>)}
        </div>
      </>,
      onSubmit: async (reason) => {
        await api.post(`/api/admin/reconciliation/findings/${finding.id}/resolution-request`, {
          reason, evidence: { source: dry?.dry_run ? "dry_run_snapshot" : "manual" },
          confirmation_text: isMega ? `${finding.user_email} · ${proposed}` : undefined,
        });
        toast.success(t("admin.recon.request_created")); refresh();
      },
    });
  };
  return (
    <Drawer open={!!finding} onClose={onClose} size="lg"
      subtitle={t("admin.recon.finding_for")} title={finding.user_email} testid="finding-drawer">
      <div className="grid grid-cols-3 gap-2">
        <Kpi label={t("admin.recon.col.stored")} value={fmtNum(finding.stored_balance)} />
        <Kpi label={t("admin.recon.col.ledger")} value={fmtNum(finding.ledger_balance)} />
        <Kpi label={t("admin.recon.col.drift")} value={`${finding.drift_amount > 0 ? "+" : ""}${fmtNum(finding.drift_amount)}`} tone={finding.drift_amount > 0 ? "danger" : "warning"} />
      </div>
      <div className="text-xs text-white/60 flex items-center gap-2 flex-wrap">
        Suspected source: <span className="text-white/90">{finding.suspected_source || "—"}</span>
        · Severity: <AutoBadge value={finding.severity} />
      </div>
      <div className="flex gap-2 flex-wrap">
        <button className="lp-btn-secondary text-xs" onClick={runDryRun} data-testid="finding-drawer-dryrun"><PlayIcon className="w-3.5 h-3.5" /> {t("admin.recon.dry_run")}</button>
        <button className="lp-btn-primary text-xs" onClick={requestResolution} data-testid="finding-drawer-request"><ClipboardCheck className="w-3.5 h-3.5" /> {t("admin.recon.request_resolution")}</button>
      </div>
      {dry && (
        <SectionCard title={t("admin.recon.dry_run")} testid="finding-drawer-dryrun-out">
          <div className="text-xs space-y-1">
            <div>Correction offered: <span className="text-white/90">{String(dry.correction_offered)}</span></div>
            <div>Proposed adjustment: <span className="font-mono text-emerald-200">{dry.proposed_adjustment > 0 ? "+" : ""}{fmtNum(dry.proposed_adjustment)}</span></div>
            <div>Projected stored: <span className="font-mono">{fmtNum(dry.projected_stored_balance_after)}</span></div>
            <div>Projected ledger: <span className="font-mono">{fmtNum(dry.projected_ledger_balance_after)}</span></div>
            {dry.conflicting_active_request && <div className="text-amber-200">Conflicting active request: {dry.conflicting_active_request.id}</div>}
          </div>
        </SectionCard>
      )}
      {detail?.active_resolution_request && (
        <SectionCard title={t("admin.recon.active_request")}>
          <div className="text-xs space-y-1">
            <div>ID: <span className="font-mono">{detail.active_resolution_request.id}</span></div>
            <div>Status: <AutoBadge value={detail.active_resolution_request.status} /></div>
            <div>Requested by: {detail.active_resolution_request.requested_by_email}</div>
            <div>Expires: <DateCell value={detail.active_resolution_request.expires_at} /></div>
          </div>
        </SectionCard>
      )}
      {detail?.investigations?.length > 0 && (
        <SectionCard title={t("admin.recon.investigations")}>
          <div className="text-xs space-y-1">
            {detail.investigations.map((inv) => (
              <div key={inv.id} className="border-t border-white/5 pt-1">
                <span className="text-white/70">{inv.source_type}</span> · confidence {inv.confidence_level} · <DateCell value={inv.created_at} />
                <div className="text-white/50">{inv.conclusion}</div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
      <ReasonModal open={!!modal} {...(modal || {})} onClose={() => setModal(null)} onSubmit={modal?.onSubmit || (async () => {})} />
    </Drawer>
  );
}

function ApprovalsTab({ refresh }) {
  const { t } = useLang();
  const [items, setItems] = React.useState(null);
  const [modal, setModal] = React.useState(null);
  const load = React.useCallback(async () => {
    const [a, b] = await Promise.all([
      api.get("/api/admin/reconciliation/resolution-requests", { params: { status: "pending_first_approval" } }).then((r) => r.data.items),
      api.get("/api/admin/reconciliation/resolution-requests", { params: { status: "pending_second_approval" } }).then((r) => r.data.items),
    ]);
    setItems([...a, ...b]);
  }, []);
  React.useEffect(() => { load(); }, [load]);
  const approve = (req) => setModal({
    title: t("admin.recon.approve_title"),
    description: `${req.user_id} · adjust ${req.proposed_adjustment > 0 ? "+" : ""}${fmtNum(req.proposed_adjustment)} · currently ${req.status}`,
    actionLabel: t("admin.recon.approve"), minLen: 10,
    onSubmit: async (reason) => { await api.post(`/api/admin/reconciliation/resolution-requests/${req.id}/approve`, { reason }); toast.success(t("admin.recon.approved")); await load(); refresh?.(); },
  });
  const reject = (req) => setModal({
    title: t("admin.recon.reject_title"), description: `${req.user_id} · reject request ${req.id.slice(0, 8)}`,
    actionLabel: t("admin.recon.reject"), danger: true, minLen: 10,
    onSubmit: async (reason) => { await api.post(`/api/admin/reconciliation/resolution-requests/${req.id}/reject`, { reason }); toast.success(t("admin.recon.rejected")); await load(); refresh?.(); },
  });
  const execute = (req) => setModal({
    title: t("admin.recon.execute_title"),
    description: `${req.user_id} · apply compensating adjustment ${req.proposed_adjustment > 0 ? "+" : ""}${fmtNum(req.proposed_adjustment)}`,
    actionLabel: t("admin.recon.execute"), danger: true, minLen: 10,
    requireCheck: true, checkLabel: t("admin.recon.execute_ack"),
    onSubmit: async () => { await api.post(`/api/admin/reconciliation/resolution-requests/${req.id}/execute`, {}); toast.success(t("admin.recon.executed")); await load(); refresh?.(); },
  });
  if (!items) return <CardSkeleton h="h-32" />;
  return (
    <div className="space-y-3">
      {items.length === 0 && <EmptyState title={t("admin.recon.no_pending")} />}
      {items.map((r) => (
        <SectionCard key={r.id} testid={`pending-${r.id.slice(0, 6)}`}
          title={<span className="flex items-center gap-2 flex-wrap"><span className="font-mono text-xs">{r.user_id}</span> <AutoBadge value={r.status} /></span>}
          description={<>Request {r.id.slice(0, 12)} · created <DateCell value={r.created_at} /> · expires <DateCell value={r.expires_at} /></>}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div><div className="text-white/50">Stored</div><div className="text-white/90 font-mono tabular-nums">{fmtNum(r.current_stored_balance)}</div></div>
            <div><div className="text-white/50">Ledger</div><div className="text-white/90 font-mono tabular-nums">{fmtNum(r.current_ledger_balance)}</div></div>
            <div><div className="text-white/50">Drift</div><div className="font-mono text-amber-200 tabular-nums">{r.drift_amount > 0 ? "+" : ""}{fmtNum(r.drift_amount)}</div></div>
            <div><div className="text-white/50">Adjustment</div><div className="font-mono text-emerald-200 tabular-nums">{r.proposed_adjustment > 0 ? "+" : ""}{fmtNum(r.proposed_adjustment)}</div></div>
          </div>
          <div className="text-xs text-white/60">
            Requested by <span className="text-white/85">{r.requested_by_email}</span>
            {r.first_approved_by_email && <> · 1st approver: <span className="text-white/85">{r.first_approved_by_email}</span></>}
          </div>
          <div className="text-xs text-white/60">Reason: <span className="text-white/85">{r.reason}</span></div>
          <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-2 text-[11px] text-amber-100 flex items-start gap-2">
            <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>This does not re-issue credits. It creates a compensating immutable ledger entry to reconcile visible balance with the ledger.</span>
          </div>
          <div className="flex gap-2 pt-1 flex-wrap">
            <button className="lp-btn-primary text-xs" onClick={() => approve(r)} data-testid={`approve-${r.id.slice(0, 6)}`}>{t("admin.recon.approve")}</button>
            {r.status === "approved" && <button className="lp-btn-danger text-xs" onClick={() => execute(r)} data-testid={`execute-${r.id.slice(0, 6)}`}>{t("admin.recon.execute")}</button>}
            <button className="lp-btn-secondary text-xs" onClick={() => reject(r)} data-testid={`reject-${r.id.slice(0, 6)}`}>{t("admin.recon.reject")}</button>
          </div>
        </SectionCard>
      ))}
      <ReasonModal open={!!modal} {...(modal || {})} onClose={() => setModal(null)} onSubmit={modal?.onSubmit || (async () => {})} />
    </div>
  );
}

export default function AdminReconciliation() {
  const { t } = useLang();
  const [tab, setTab] = React.useState("overview");
  const [openFinding, setOpenFinding] = React.useState(null);
  const [nonce, setNonce] = React.useState(0);
  const refresh = () => setNonce((x) => x + 1);
  return (
    <div className="space-y-6" data-testid="admin-reconciliation-page">
      <PageHeader title={t("admin.recon.title")} description={t("admin.recon.subtitle")} />
      <TabList value={tab} onChange={setTab} tabs={[
        { id: "overview", label: t("admin.recon.tab.overview") },
        { id: "findings", label: t("admin.recon.tab.findings") },
        { id: "approvals", label: t("admin.recon.tab.approvals") },
      ]} />
      {tab === "overview" && <OverviewTab key={`ov-${nonce}`} />}
      {tab === "findings" && <FindingsTab onOpenFinding={setOpenFinding} key={`fn-${nonce}`} />}
      {tab === "approvals" && <ApprovalsTab refresh={refresh} key={`ap-${nonce}`} />}
      <FindingDrawer finding={openFinding} onClose={() => setOpenFinding(null)} refresh={refresh} />
    </div>
  );
}
